// Cost tab, the on-chain footprint of each spend path: script bytes, the
// control block (taproot merkle path), witness bytes, the worst-case witness
// weight (WU) and the corresponding virtual bytes (WU / 4). Numerics are right-
// aligned and monospaced; a worst-case footer summarizes the contract.

import { Coins } from "lucide-react";
import { useIde } from "../store";
import type { Cost } from "../types";
import { EmptyState, SectionHead, runHint } from "./shared";

/** Witness units → virtual bytes, fixed to 2 decimals (the fee-estimation unit). */
function vbytes(weightUnits: number): string {
  return (weightUnits / 4).toFixed(2);
}

export function CostPanel() {
  const result = useIde((s) => s.result);
  const costs = result?.costs;

  if (!costs || costs.length === 0) {
    return (
      <div className="pn-scroll">
        <EmptyState
          icon={<Coins size={22} strokeWidth={1.5} />}
          title="No cost analysis yet"
          hint={runHint("the per-leaf on-chain cost")}
        />
      </div>
    );
  }

  // A spend reveals exactly one leaf, so the worst case over the tree is the
  // largest leaf, not the sum of them (which is a number no spend ever pays).
  const worstScript = costs.reduce((a, c) => Math.max(a, c.scriptBytes), 0);
  const worstWeight = costs.reduce((a, c) => Math.max(a, c.maxWitnessWeight), 0);
  const worstInput = costs.reduce((a, c) => Math.max(a, c.maxInputWeight), 0);

  return (
    <div className="pn-scroll">
      <SectionHead
        title="On-chain cost"
        count={`${costs.length} ${costs.length === 1 ? "leaf" : "leaves"}`}
      />
      <div className="pn-card">
        <table className="pn-cost-table">
        <thead>
          <tr>
            <th scope="col">Spend path</th>
            <th scope="col" className="is-num">
              Script&nbsp;B
            </th>
            <th scope="col" className="is-num">
              Control&nbsp;B
            </th>
            <th scope="col" className="is-num">
              Witness&nbsp;B
            </th>
            <th scope="col" className="is-num">
              Witness&nbsp;vB
            </th>
            <th scope="col" className="is-num">
              Input&nbsp;vB
            </th>
          </tr>
        </thead>
        <tbody>
          {costs.map((c: Cost) => (
            <tr key={c.name}>
              <td className="is-name">{c.name}</td>
              <td className="is-num">{c.scriptBytes}</td>
              <td className="is-num">{c.controlBytes}</td>
              <td className="is-num">{c.witnessBytes}</td>
              <td className="is-num pn-cost-vb">{vbytes(c.maxWitnessWeight)}</td>
              <td className="is-num pn-cost-vb">{vbytes(c.maxInputWeight)}</td>
            </tr>
          ))}
        </tbody>
        {costs.length > 1 && (
          <tfoot>
            <tr>
              <td className="is-name">Worst case</td>
              <td className="is-num">{worstScript}</td>
              <td className="is-num pn-cost-dash">·</td>
              <td className="is-num pn-cost-dash">·</td>
              <td className="is-num pn-cost-vb">{vbytes(worstWeight)}</td>
              <td className="is-num pn-cost-vb">{vbytes(worstInput)}</td>
            </tr>
          </tfoot>
        )}
        </table>
      </div>
      <p className="pn-cost-note">
        Virtual bytes (vB) are weight ÷ 4, the unit fee estimators bill against. Witness vB is the
        satisfaction (signatures, revealed script, control block) and is what varies between paths;
        Input vB adds the input&apos;s fixed 41 vB base (outpoint + sequence) for the true cost to spend
        this path. Neither includes the shared transaction overhead (version, outputs, locktime).
      </p>
    </div>
  );
}
